import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import Image from 'next/image';
import router, { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import { InstantSearch } from 'react-instantsearch';
import algoliasearch from 'algoliasearch/lite';
import { INavigation } from '@features/navigation/typescript';
import styles from '@features/navigation/components/Navigation/Navigation.styles';
import { TCategoriesTree, TServiceMap } from '@features/categories/typescript';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import CartNavigationButton from '@features/cart/components/CartNavigationButton/CartNavigationButton';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import Search from '@components/icons/components/Search';
import Menu from '@components/icons/components/Menu';
import { BASE_PATH } from '@config/index';
import getImageQuality from '@utils/getImageQuality';
import IsCustomerMiddleware from '@utils/IsCustomerMiddleware';
import { CLIENT_ID, CLIENT_KEY } from '@components/Search/const';
import { SearchBox } from '@components/Search/components/SearchBox/SearchBox';
import { useResponsive } from '@features/responsive';
const DynamicSearch = dynamic(() => import('@components/Search/Search'), {
  ssr: false
});
const DynamicMegaMenuMobile = dynamic(() => import('@features/navigation/components/MegaMenuMobile/MegaMenuMobile'), {
  ssr: false
});
type PropsType = {
  navElements: INavigation;
  categoriesTree: TCategoriesTree;
  serviceMap: TServiceMap;
  isTransparent: boolean;
  backgroundColor?: string;
};
const NavigationMobile = ({
  navElements,
  categoriesTree,
  serviceMap,
  isTransparent,
  backgroundColor = undefined
}: PropsType) => {
  const {
    isSearchOpened,
    openSearch,
    isMenuMobileOpened,
    setMenuMobileOpened,
    closeSearch,
    userData,
    setUserData,
    setUserPanelOpened
  } = layoutUiState;
  const {
    data: session
  } = useSession();
  const {
    pathname
  } = useRouter();
  const {
    t
  } = useTranslation('common');
  const {
    isWiderThanDesktop
  } = useResponsive();
  const client = useMemo(() => algoliasearch(CLIENT_ID, CLIENT_KEY), [CLIENT_ID, CLIENT_KEY]);
  const [query, setQuery] = useState('');
  const iconColor = isTransparent ? 'neutralWhite' : 'darkGrey700';
  const inputColor = isTransparent ? 'neutralWhite' : 'lightGrey700';
  const showSearch = () => <>
      <Button cy="MobileSearchBarButton" variant="iconOnly" variantColor={isTransparent ? 'white' : 'dark'} onClick={openSearch} addSx={styles.menuSearchIcon} addClassName="nav_searchbar-button">
        <Icon size="22px" color={iconColor}>
          <Search />
        </Icon>
      </Button>
      <div sx={styles.menuSearchInputContainer}>
        <input sx={styles.menuSearchInput(iconColor, inputColor)} placeholder={t('search')} onClick={openSearch} onFocus={e => e.preventDefault()} readOnly className="nav_searchbar" />
        <Icon size="20px" color={iconColor} addSx={styles.searchIcon}>
          <Search />
        </Icon>
      </div>
    </>;
  const handlerMenuMobileOpen = () => {
    setUserData(false);
    setMenuMobileOpened(true);
  };
  const handleOpenUserPanel = () => {
    setMenuMobileOpened(true);
    setUserData(session || true);
  };
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get('panel') === 'true' && session) {
      handleOpenUserPanel();
      delete router.query.panel;
      router.replace({
        pathname: router.pathname,
        query: router.query
      }, undefined, {
        shallow: true
      });
    }
    router.beforePopState(({
      as
    }) => {
      if (as !== router.asPath) {
        closeSearch();
        setMenuMobileOpened(false);
      }
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [pathname, session]);
  const searchPlaceHolder = isWiderThanDesktop ? t('search_placeholder') : t('what_are_you_looking_for');
  return <>
      <nav sx={styles.navigationContainerMobile(isTransparent, backgroundColor ?? null, iconColor === 'neutralWhite')}>
        <div sx={styles.navigationMobilePadding}>
          <div sx={styles.navigationContentContainer}>
            <Button variant="iconOnly" variantColor={isTransparent ? 'white' : 'dark'} addSx={styles.menuHamburgerIcon} onClick={() => handlerMenuMobileOpen()} addClassName="nav_menu_mobile-button" cy="NavigationMenuMobileButton">
              <Icon size="24px" color={iconColor}>
                <Menu />
              </Icon>
            </Button>

            <div sx={styles.menuLogoMobile}>
              <Link href="/" onClick={() => setMenuMobileOpened(false)}>
                <Image data-cy="logoIcon" fill sizes="100vh" src={`${BASE_PATH}/img/logo.svg`} quality={getImageQuality()} alt={t('logoIconAlt')} priority />
              </Link>
            </div>
            {showSearch()}
            <IsCustomerMiddleware>
              <CartNavigationButton iconColor={iconColor} active={pathname !== '/cart'} isDark={!isTransparent} />
            </IsCustomerMiddleware>
          </div>
        </div>
      </nav>
      {/* @ts-expect-error - React 19 compatibility issue with next/dynamic types */}
      <DynamicMegaMenuMobile serviceMap={serviceMap} navElements={navElements} categoriesTree={categoriesTree} isVisible={isMenuMobileOpened} userData={userData} handleOpenUserPanel={handleOpenUserPanel} setUserPanelOpened={setUserPanelOpened} />
      <div sx={styles.mainContainer(isSearchOpened)} data-cy="SearchModal">
        <InstantSearch searchClient={client} future={{
        preserveSharedStateOnUnmount: false
      }} insights>
          <SearchBox placeholder={searchPlaceHolder} setQuery={setQuery} isSearchOpened={isSearchOpened} />
          {isSearchOpened &&
        // @ts-expect-error - React 19 compatibility issue with next/dynamic types
        <DynamicSearch serviceMap={serviceMap} query={query} />}
        </InstantSearch>
      </div>
    </>;
};
export default observer(NavigationMobile);